import { IntlShape } from 'react-intl';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { isSmartLinkDraft } from '../../utils/isSmartLinkDraft';
import { editorMessages } from '../editorMessages';

/**
 * The publish state of the page (design a1, a2, a3): never published, live with the draft equal to
 * the live page, or live with changes that wait. `count` is null when the API gives no number (an
 * older server).
 */
export type EditorStatus = { kind: 'draft' } | { kind: 'upToDate' } | { kind: 'pending'; count: number | null };

export function editorStatus(smartLink: SerializedSmartLink): EditorStatus {
  if (smartLink.publishedSmartLinkVersion === null) {
    return { kind: 'draft' };
  }
  const { pendingChanges } = smartLink;
  if (typeof pendingChanges === 'number') {
    return pendingChanges > 0 ? { kind: 'pending', count: pendingChanges } : { kind: 'upToDate' };
  }
  // An older server sends no count. A draft recent version still says that changes wait.
  return isSmartLinkDraft(smartLink) ? { kind: 'pending', count: null } : { kind: 'upToDate' };
}

/** The words of the status pill. */
export function editorStatusLabel(intl: IntlShape, status: EditorStatus): string {
  switch (status.kind) {
    case 'draft':
      return intl.formatMessage(editorMessages.statusDraft);
    case 'upToDate':
      return intl.formatMessage(editorMessages.statusUpToDate);
    case 'pending':
      return status.count === null
        ? intl.formatMessage(editorMessages.statusPendingNoCount)
        : intl.formatMessage(editorMessages.statusPending, { count: status.count });
  }
}

/** "Publish changes" is on for a page that was never published (with blocks) and for changes that wait. */
export function canPublish(status: EditorStatus, blockCount: number): boolean {
  if (status.kind === 'draft') {
    return blockCount > 0;
  }
  return status.kind === 'pending';
}
